import { useRouter } from "next/router";
import { cn } from "@/libs/utils";
import Link from "next/link";
import { useEffect, useRef } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { Dot } from "@/v2-components/dot";
import { useTrans } from "@/utils/useTrans";

// 탭 노출 순서. label 은 gnb 네임스페이스 번역 키이고, href 는 trailingSlash 설정
// 때문에 끝에 `/` 가 필요하다 (router.asPath 와 정확히 비교하므로).
//
// - Home 다음은 AI Powered (#5038 리디자인, 플래그십). 옛 Audio-to-Haptics 는 끝에 보존.
// - PC 그룹(PC Game · Sim Racing)은 VR 그룹보다 앞. GNB 드롭다운의 PC-VR 순서와 동일 (#5066).
//   단 GNB 는 그룹 헤더 · Game Keys · AI Powered 위치가 달라 전체 순서가 같지는 않다.
//
// ★**`Home`(`/games/`) 항목을 뺐다** (#5795). #5683 이 `src/pages/games/index.tsx` 를
//   `→ /games/pc/` 리다이렉트 스텁으로 바꿔 그 탭이 가리킬 페이지가 없어졌다.
//
//   ⚠️ **`/games/pc/` 로 재지정하지 않는 이유** — 이 파일의 `href` 는 목적지이면서
//   **활성 판정 키**를 겸한다(바로 아래 `router.asPath === item.href`). `PC Game` 과 값이
//   같아지면 `find` 가 앞선 `Home` 을 먼저 집어 PC 페이지에서 `Home` 이 활성으로 칠해진다.
//
//   ⚠️ 이 탭바는 `tabBar` prop 을 **생략한** 소비처에서만 폴백으로 그려진다
//   (`GamesPageTemplate.tsx:104` · `GameDetailPageTemplate.tsx:191` 의 `tabBar ?? <GamesTabWrap />`).
//   2026-09-17 기준 생략하는 소비처는 `pages/games/ai-powered.tsx` · `audio-to-haptic.tsx`
//   **둘뿐이고 둘 다 `next.config.mjs` 리다이렉트 규칙이라 도달 불가**다 — 즉 지금은
//   사용자 영향이 0이지만, 라이브 페이지가 `tabBar` 를 생략하는 순간 되살아난다.
const tabItems: {
  label: string;
  href: string;
  dotted?: boolean;
}[] = [
  { label: "PC Game", href: "/games/pc/" },
  { label: "VR Game", href: "/games/vr/" },
] as const;

const useStore = create(
  persist<{
    scrollLeft: number;
    setScrollLeft: (scrollLeft: number) => void;
  }>(
    (set) => ({
      scrollLeft: 0,
      setScrollLeft: (scrollLeft: number) => set((state) => ({ scrollLeft })),
    }),
    {
      name: "tab-menus1-storage",
    },
  ),
);

const GamesTabMenu = () => {
  const containerRef = useRef<any>(null);
  const router = useRouter();
  const { t } = useTrans(["gnb"]);
  // 폴백은 **존재하는 탭**이어야 한다 — 종전 `"Home"` 은 위에서 지운 항목이라
  // 아무 탭도 활성으로 안 칠해진다(#5795).
  const activeTab =
    tabItems.find((item) => router.asPath === item.href)?.label ?? "PC Game";
  const { scrollLeft, setScrollLeft } = useStore();

  // 탭을 클릭했을 때 스크롤 위치를 저장
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = scrollLeft;
    }
  }, []);

  // nav 에 있는 탭을 눌러서 진입했을때 처리용으로
  // 완전히 탭이 안보이는 경우만 이동
  useEffect(() => {
    if (containerRef.current && activeTab) {
      const activeElement = document.getElementById(activeTab);
      if (activeElement) {
        const activeRight =
          activeElement.offsetLeft + activeElement.clientWidth;
        const containerRight =
          containerRef.current.scrollLeft + containerRef.current.clientWidth;

        if (
          activeRight < containerRef.current.scrollLeft ||
          activeElement.offsetLeft > containerRight
        ) {
          activeElement.scrollIntoView({ behavior: "auto", block: "nearest" });
          setScrollLeft(containerRef.current.scrollLeft);
        }
      }
    }
  }, [activeTab]);

  return (
    <div ref={containerRef} className="scrollbar-none overflow-x-auto">
      <div className="w-max text-base font-400 text-coolgray-50">
        <ul className="flex list-none gap-6 px-4 md:gap-7 laptop:pr-0 laptop:pt-0 max:px-0">
          {tabItems.map((item) => (
            <li
              key={item.label}
              id={item.label}
              className={cn(
                "flex cursor-pointer whitespace-nowrap",
                item.label === activeTab
                  ? "border-b-2 border-blue-60 text-coolgray-90"
                  : "hover:text-coolgray-70",
              )}
            >
              <Link
                scroll
                href={item.href}
                className="py-[11px] md:py-[21px]"
                onClick={(e) => {
                  if (containerRef.current) {
                    setScrollLeft(containerRef.current.scrollLeft);
                  }
                }}
              >
                <p className="relative">
                  {t(item.label)}
                  <Dot visible={item.dotted} />
                </p>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default GamesTabMenu;
